import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { NovaButton } from '@rds-nova/angular-button';
import { NovaIcon } from '@rds-nova/angular-icon';
import {
  NovaBanner,
  NovaBannerActions,
  NovaBannerBody,
  NovaBannerContainer,
  NovaBannerContent,
  NovaBannerLeftSection,
  NovaBannerSubtext,
  NovaBannerTitle,
} from '@rds-nova/angular-banner';
import { take } from 'rxjs';

import {
  selectIsRemoteSigning,
  selectSigningJoinCode,
} from 'src/app/features/session/store/selectors/session.selectors';
import { SigningJoinCodeService } from 'src/app/features/participant-selection/services/signing-join-code.service';
import { TrackedButtonDirective } from 'src/app/core/analytics/directives/tracked-button.directive';
import { AppState } from 'src/app/store';

@Component({
  selector: 'app-share-link-banner',
  templateUrl: './share-link-banner.component.html',
  styleUrls: ['./share-link-banner.component.scss'],
  imports: [
    NovaBanner,
    NovaBannerActions,
    NovaBannerBody,
    NovaBannerContainer,
    NovaBannerContent,
    NovaBannerLeftSection,
    NovaBannerSubtext,
    NovaBannerTitle,
    NovaButton,
    NovaIcon,
    TrackedButtonDirective,
    TranslatePipe,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareLinkBannerComponent {
  private readonly appStore = inject(Store<AppState>);
  private readonly joinCodeService = inject(SigningJoinCodeService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isRemote = this.appStore.selectSignal(selectIsRemoteSigning);
  private readonly joinCode = this.appStore.selectSignal(selectSigningJoinCode);

  readonly participantCount = signal<number | null>(null);
  readonly copied = signal(false);

  readonly canShare = computed(
    () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
  );

  readonly shareUrl = computed(() => {
    const code = this.joinCode();
    return code ? `${window.location.origin}/join/${code}` : '';
  });

  readonly isVisible = computed(
    () =>
      this.isRemote() &&
      !!this.joinCode() &&
      (this.participantCount() ?? 0) > 1,
  );

  constructor() {
    afterNextRender(() => {
      const code = this.joinCode();
      if (!code) {
        return;
      }

      this.joinCodeService
        .resolve(code)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.participantCount.set(response.participants?.length ?? 0);
          },
          error: () => {
            this.participantCount.set(0);
          },
        });
    });
  }

  async onShareOrCopy(): Promise<void> {
    const url = this.shareUrl();
    if (!url) {
      return;
    }

    if (this.canShare()) {
      try {
        await navigator.share({ url });
      } catch {
        // User cancelled share — not an error
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 3000);
    } catch {
      // Clipboard API blocked — fail silently
    }
  }
}
